import { useState } from 'react';
import { trpc } from '../utils/trpc';
import styles from '../styles/Notificationsender.module.css';

type NotificationType = 'info' | 'group_invite' | 'group_post';
type VenueNotificationType = 'venue_message' | 'venue_request';
type TargetMode = 'member' | 'venue';

interface NotificationSenderProps {
  groupId?: string;
  defaultType?: NotificationType;
  onSent?: () => void;
}

export default function NotificationSender({
  groupId,
  defaultType = 'info',
  onSent,
}: NotificationSenderProps) {
  const [mode, setMode] = useState<TargetMode>('member');
  const [recipient, setRecipient] = useState('');
  const [memberType, setMemberType] = useState<NotificationType>(defaultType);
  const [venueType, setVenueType] = useState<VenueNotificationType>('venue_message');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSuccess = () => {
    setStatus('success');
    setRecipient('');
    setMessage('');
    setTimeout(() => setStatus('idle'), 3000);
    onSent?.();
  };

  const onError = (err: { message: string }) => {
    setStatus('error');
    setErrorMsg(err.message);
    setTimeout(() => setStatus('idle'), 4000);
  };

  const sendMember = trpc.notification.send.useMutation({ onSuccess, onError });
  const sendVenue = trpc.notification.sendToVenue.useMutation({
    onSuccess,
    onError,
  });

  const isPending = sendMember.isPending || sendVenue.isPending;

  const handleSend = () => {
    if (!recipient.trim() || !message.trim()) return;
    setStatus('idle');
    setErrorMsg('');

    if (mode === 'member') {
      sendMember.mutate({
        recipientUsername: recipient.trim(),
        type: memberType,
        message: message.trim(),
        ...(groupId && { groupId }),
      });
    } else {
      sendVenue.mutate({
        recipientUsername: recipient.trim(),
        type: venueType,
        message: message.trim(),
        ...(groupId && { groupId }),
      });
    }
  };

  const MEMBER_TYPES: { value: NotificationType; label: string }[] = [
    { value: 'info', label: 'Info' },
    { value: 'group_invite', label: 'Group Invite' },
    { value: 'group_post', label: 'Group Post' },
  ];

  const VENUE_TYPES: { value: VenueNotificationType; label: string }[] = [
    { value: 'venue_message', label: 'Message' },
    { value: 'venue_request', label: 'Request' },
  ];

  return (
    <div className={styles.sender}>
      <div className={styles.header}>
        <div className={styles.headerDot} />
        <h3 className={styles.headerTitle}>Send Notification</h3>
      </div>

      <div className={styles.body}>
        {/* Mode switcher */}
        <div className={styles.modeSwitcher}>
          <button
            className={`${styles.modeBtn} ${mode === 'member' ? styles.modeActive : ''}`}
            onClick={() => setMode('member')}
          >
            Member
          </button>
          <button
            className={`${styles.modeBtn} ${mode === 'venue' ? styles.modeActive : ''}`}
            onClick={() => setMode('venue')}
          >
            Venue Owner
          </button>
        </div>

        {/* Recipient */}
        <div className={styles.field}>
          <label className={styles.label}>
            {mode === 'member' ? 'Member Username' : 'Venue Owner Username'}
          </label>
          <input
            className={styles.input}
            placeholder="username"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </div>

        {/* Type */}
        <div className={styles.field}>
          <label className={styles.label}>Type</label>
          <div className={styles.typeRow}>
            {mode === 'member'
              ? MEMBER_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`${styles.typeBtn} ${memberType === value ? styles.typeActive : ''}`}
                    onClick={() => setMemberType(value)}
                  >
                    {label}
                  </button>
                ))
              : VENUE_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`${styles.typeBtn} ${venueType === value ? styles.typeActive : ''}`}
                    onClick={() => setVenueType(value)}
                  >
                    {label}
                  </button>
                ))}
          </div>
        </div>

        {/* Message */}
        <div className={styles.field}>
          <label className={styles.label}>Message</label>
          <textarea
            className={styles.textarea}
            placeholder="Write a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
          />
        </div>

        {status === 'success' && (
          <div className={styles.successBanner}>✓ Notification sent</div>
        )}
        {status === 'error' && (
          <div className={styles.errorBanner}>{errorMsg || 'Failed to send'}</div>
        )}

        <button
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!recipient.trim() || !message.trim() || isPending}
        >
          {isPending ? 'Sending...' : 'Send Notification'}
        </button>
      </div>
    </div>
  );
}
